// マグネット選択: エッジに沿う最短経路 (live-wire, Dijkstra) を縮小プロキシ上で求める。純ロジック。
import type { Pt } from '@/types/geometry'
import type { QuickProxy } from './quickSelect'

/**
 * from → to の経路を、エッジ (proxy.edge) の強い画素を通るように探索する。
 * 探索窓は両端点の外接矩形 + margin に限定する。戻り値はプロキシ座標の点列 (from を含み to で終わる)。
 */
export function liveWire(p: QuickProxy, from: Pt, to: Pt, margin = 24, band = 10): Pt[] {
  const { w, h, edge } = p
  const fx = Math.max(0, Math.min(w - 1, Math.round(from.x))), fy = Math.max(0, Math.min(h - 1, Math.round(from.y)))
  const tx = Math.max(0, Math.min(w - 1, Math.round(to.x))), ty = Math.max(0, Math.min(h - 1, Math.round(to.y)))
  const x0 = Math.max(0, Math.min(fx, tx) - margin), y0 = Math.max(0, Math.min(fy, ty) - margin)
  const x1 = Math.min(w - 1, Math.max(fx, tx) + margin), y1 = Math.min(h - 1, Math.max(fy, ty) + margin)
  const ww = x1 - x0 + 1, wh = y1 - y0 + 1
  // 画素コスト: エッジが強いほど小さい (0.05..1)
  let maxE = 1
  for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) maxE = Math.max(maxE, edge[y * w + x]!)
  // 画素コスト = エッジの弱さ + 線分からの距離のペナルティ (帯の外へ大きく逸れない)
  const cost = new Float32Array(ww * wh)
  const sdx = tx - fx, sdy = ty - fy
  const slen2 = sdx * sdx + sdy * sdy || 1
  for (let y = 0; y < wh; y++) for (let x = 0; x < ww; x++) {
    const gx = x0 + x, gy = y0 + y
    const t = Math.max(0, Math.min(1, ((gx - fx) * sdx + (gy - fy) * sdy) / slen2))
    const dist = Math.hypot(gx - (fx + sdx * t), gy - (fy + sdy * t))
    const pen = (dist / band) * (dist / band)
    cost[y * ww + x] = 0.05 + (1 - edge[gy * w + gx]! / maxE) + pen
  }
  const dist = new Float32Array(ww * wh).fill(Infinity)
  const prev = new Int32Array(ww * wh).fill(-1)
  const done = new Uint8Array(ww * wh)
  // 二分ヒープ
  const heap: number[] = []
  const hd: number[] = []
  const push = (i: number, d: number) => {
    heap.push(i); hd.push(d)
    let k = heap.length - 1
    while (k > 0) {
      const pk = (k - 1) >> 1
      if (hd[pk]! <= hd[k]!) break
      ;[heap[pk], heap[k]] = [heap[k]!, heap[pk]!]
      ;[hd[pk], hd[k]] = [hd[k]!, hd[pk]!]
      k = pk
    }
  }
  const pop = (): number => {
    const top = heap[0]!
    const li = heap.pop()!, ld = hd.pop()!
    if (heap.length) {
      heap[0] = li; hd[0] = ld
      let k = 0
      for (;;) {
        const l = k * 2 + 1, r = l + 1
        let m = k
        if (l < heap.length && hd[l]! < hd[m]!) m = l
        if (r < heap.length && hd[r]! < hd[m]!) m = r
        if (m === k) break
        ;[heap[m], heap[k]] = [heap[k]!, heap[m]!]
        ;[hd[m], hd[k]] = [hd[k]!, hd[m]!]
        k = m
      }
    }
    return top
  }
  const s = (fy - y0) * ww + (fx - x0), t = (ty - y0) * ww + (tx - x0)
  dist[s] = 0
  push(s, 0)
  while (heap.length) {
    const i = pop()
    if (done[i]) continue
    done[i] = 1
    if (i === t) break
    const x = i % ww, y = (i - x) / ww
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      if (!dx && !dy) continue
      const nx = x + dx, ny = y + dy
      if (nx < 0 || ny < 0 || nx >= ww || ny >= wh) continue
      const j = ny * ww + nx
      if (done[j]) continue
      const nd = dist[i]! + cost[j]! * (dx && dy ? 1.4142 : 1)
      if (nd < dist[j]!) {
        dist[j] = nd
        prev[j] = i
        push(j, nd)
      }
    }
  }
  const out: Pt[] = []
  let cur = t
  if (prev[t]! < 0 && t !== s) return [{ x: fx, y: fy }, { x: tx, y: ty }]
  while (cur >= 0) {
    out.push({ x: (cur % ww) + x0, y: Math.floor(cur / ww) + y0 })
    if (cur === s) break
    cur = prev[cur]!
  }
  out.reverse()
  return out
}

/** 点列を間引く (Douglas-Peucker、許容 eps) */
export function simplifyPath(pts: Pt[], eps = 0.8): Pt[] {
  if (pts.length < 3) return pts.slice()
  const keep = new Uint8Array(pts.length)
  keep[0] = 1; keep[pts.length - 1] = 1
  const stack: [number, number][] = [[0, pts.length - 1]]
  while (stack.length) {
    const [a, b] = stack.pop()!
    const A = pts[a]!, B = pts[b]!
    const len = Math.hypot(B.x - A.x, B.y - A.y) || 1
    let best = -1, bd = eps
    for (let i = a + 1; i < b; i++) {
      const P = pts[i]!
      const d = Math.abs((B.x - A.x) * (A.y - P.y) - (A.x - P.x) * (B.y - A.y)) / len
      if (d > bd) { bd = d; best = i }
    }
    if (best > 0) { keep[best] = 1; stack.push([a, best], [best, b]) }
  }
  return pts.filter((_, i) => keep[i])
}
